import { useEffect, useMemo, useState } from 'react';
import { WeekPanel } from './WeekPanel';
import { useShifts } from '../staff/useShifts';
import { useHolidays } from '../staff/useHolidays';
import { leaveByDate } from '../staff/leave';
import { useWindowedBookings } from '../bookings/useWindowedBookings';
import { occupancyByDate } from '../bookings/domain';
import { addWeeks, startOfWeek, weekDates } from '../staff/week';
import { weekLoadState } from './weekLoad';
import type { Staff } from '../staff/types';

// The On-duty panel's data, and the reason this container exists at all.
//
// The Daily page fetches on a ±2-DAY window (statWindow). This panel shows a
// FRIDAY→THURSDAY WEEK — up to six of its seven days fall outside that window,
// and next week falls wholly outside it. The rail hides the gap, because
// onDutyTeams filters to a single day which ±2 covers comfortably; it only
// opens when the panel does.
//
// LAZY BY MOUNTING, not by a flag. The panel is closed by default and most days
// nobody opens it, so fetching a week of shifts and a week of bookings on every
// Daily load would double the page's request weight to serve something usually
// shut. TodayView mounts this the first time the panel is opened and keeps it
// mounted after — which is also what the reveal animation needs, since a
// conditionally rendered child cannot be animated, it can only appear.
//
// Both hooks below take an arbitrary window and both HOLD their previous set
// across a window change rather than blanking, which is exactly what a week
// stepper needs — and is why this must not roll its own fetch.

export interface WeekPanelDataProps {
  anchor: string;
  offset: number;
  onStep: (delta: number) => void;
  staff: Staff[];
  today: string;
  onOpenRota: (weekStart: string) => void;
  onPickHelper?: (s: Staff) => void;
}

export function WeekPanelData({ anchor, offset, onStep, staff, today, onOpenRota, onPickHelper }: WeekPanelDataProps) {
  // The week containing the page's anchor, stepped by the panel's own offset.
  // startOfWeek is the roster's Friday-first maths — never recomputed here, so
  // the panel and the rota can never disagree about where a week begins.
  const days = useMemo(() => weekDates(addWeeks(startOfWeek(anchor), offset)), [anchor, offset]);
  const from = days[0]!;
  const to = days[6]!;

  const { shifts, window: shiftsWindow, failed: shiftsFailed } = useShifts(from, to);
  const { bookings, window: bookingsWindow, failed: bookingsFailed } = useWindowedBookings({ from, to });
  // Leave, from the same source the rota reads. Without it a week containing
  // approved leave looked like ordinary unfilled staffing — the mirror was
  // quietly dropping the one thing that explains a thin day (PR #164, codex P2).
  const { holidays, window: leaveWindow, failed: leaveFailed } = useHolidays(from, to);

  // SETTLED MEANS "THIS WEEK'S DATA", NOT "NOTHING IN FLIGHT". All three hooks
  // hold their previous window across a change, so any one of them can be
  // serving last week while the others have moved on. weekLoadState compares
  // each set's own window against the requested one, and reports a failure as a
  // failure rather than letting a short roster or an undercounted dog load pass
  // for the week's own quiet truth (PR #164, codex P2 ×3).
  const requested = useMemo(() => ({ from, to }), [from, to]);
  const { loading, failed } = weekLoadState({
    requested,
    shifts: shiftsWindow,
    bookings: bookingsWindow,
    leave: leaveWindow,
    failed: shiftsFailed || bookingsFailed || leaveFailed,
  });

  // HOLD THE WEEK YOU CAN SEE until the next one is complete (Freddi,
  // 2026-08-17 — "navigating next and previous weeks flickers rather than
  // smoothly shows each week like all other elements"). The requested `days`
  // change on the instant of the click, so the column headers jumped to the new
  // week while the shifts, dogs and leave under them were still the old one's —
  // a grid briefly describing no week at all, then rearranging itself. Every
  // other stepper on this page holds its content, dims, and swaps once, and the
  // week is not the exception.
  // HOLD THE WHOLE WEEK, NOT JUST ITS DATES (Freddi, 2026-08-17 — the week
  // stepper "flickers rather than smoothly shows each week"; PR #166, codex P2
  // for the incoherence below).
  //
  // Holding only the column headers was half an answer. The three hooks settle
  // INDEPENDENTLY, so a step where shifts and leave arrive and bookings fails
  // left the old dates drawn against the new week's roster and the old week's
  // dog counts — three sources, three different weeks, in one grid. What is
  // held has to be the whole snapshot, and it swaps in one move or not at all.
  const live = useMemo(
    () => ({
      days,
      shifts,
      dogsByDate: occupancyByDate(bookings, days),
      leaveFor: leaveByDate(holidays, days),
    }),
    [days, shifts, bookings, holidays],
  );
  const [held, setHeld] = useState(live);
  useEffect(() => {
    // SUCCESSFUL settlement, not merely "not loading". weekLoadState reports a
    // failure as {loading:false, failed:true} — deliberately, so the panel says
    // so rather than spinning for ever — and gating on `!loading` alone let a
    // failed step swap the grid to a week whose data never arrived (PR #165,
    // codex P2).
    if (!loading && !failed) setHeld(live);
  }, [loading, failed, live]);
  // On a cold load there is nothing to hold, so the requested week is drawn and
  // its cells simply fill as the sets land.
  const view = (loading || failed) && held.days.length === 7 ? held : live;

  return (
    <WeekPanel
      days={view.days}
      offset={offset}
      onStep={onStep}
      shifts={view.shifts}
      staff={staff}
      dogsByDate={view.dogsByDate}
      leaveFor={view.leaveFor}
      today={today}
      onOpenRota={() => onOpenRota(from)}
      onPickHelper={onPickHelper}
      loading={loading}
      failed={failed}
    />
  );
}
